/* ===================================================================
   NotificationBell -- the topbar bell + a dropdown of recent alerts
   (Brad, 2026-06-17).

   Reads GET /api/v1/notifications (live) and marks read via
   POST /api/v1/notifications/<id>/read + /read-all. The red dot shows only
   when unread_count > 0. Clicking a row marks it read; "Mark all read"
   clears them all. After any write the feed + badge refetch.
   =================================================================== */
import { useEffect, useRef, useState } from 'react';
import { useNotifications, useMarkNotifications } from '../hooks';
import { relativeTime } from '../lib/display';

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const q = useNotifications({ limit: 30 });
  const mark = useMarkNotifications();

  const unread = q.data?.unread_count ?? 0;
  const items = q.data?.notifications ?? [];

  // Close the dropdown on an outside click.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  function handleRowClick(id: string, read: boolean) {
    if (!read) void mark.markRead(id).catch(() => {});
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="iconbtn"
        aria-label="Notifications"
        onClick={() => setOpen((o) => !o)}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9}>
          <path d="M12 3a6 6 0 016 6v4l2 3H4l2-3V9a6 6 0 016-6zM9.5 19a2.5 2.5 0 005 0" strokeLinejoin="round" />
        </svg>
        {unread > 0 && <span className="dot" />}
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 8px)',
            width: 340,
            maxHeight: 420,
            overflow: 'auto',
            background: 'var(--surface, #fff)',
            border: '1px solid var(--line, rgba(0,0,0,.1))',
            borderRadius: 12,
            boxShadow: '0 12px 32px rgba(0,0,0,.18)',
            zIndex: 50,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderBottom: '1px solid var(--line, rgba(0,0,0,.08))',
            }}
          >
            <b style={{ fontSize: 14 }}>Notifications</b>
            {unread > 0 && (
              <button
                className="mini mini--ghost"
                onClick={() => void mark.markAllRead().catch(() => {})}
                disabled={mark.isPending}
              >
                Mark all read
              </button>
            )}
          </div>

          {q.isLoading ? (
            <div className="muted" style={{ padding: 16, fontSize: 13 }}>Loading...</div>
          ) : items.length === 0 ? (
            <div className="muted" style={{ padding: 16, fontSize: 13 }}>You are all caught up.</div>
          ) : (
            items.map((n) => {
              const read = Boolean(n.read_at);
              return (
                <button
                  key={n.id}
                  onClick={() => handleRowClick(n.id, read)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '11px 14px',
                    border: 'none',
                    borderBottom: '1px solid var(--line, rgba(0,0,0,.05))',
                    background: read ? 'transparent' : 'rgba(53,99,255,.06)',
                    cursor: read ? 'default' : 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                    {!read && (
                      <span
                        style={{ width: 7, height: 7, borderRadius: 4, background: 'var(--accent, #3563ff)', flex: 'none', marginTop: 4 }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: read ? 400 : 600, fontSize: 13 }}>{n.title}</div>
                      {n.body && (
                        <div className="muted" style={{ fontSize: 12, lineHeight: 1.45, marginTop: 2 }}>{n.body}</div>
                      )}
                      <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>{relativeTime(n.created_at)}</div>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
